import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../App.css";
import { useLanguage } from "../../context/LanguageContext";
import { usePhotos } from "../../context/PhotoContext";
import Swal from "sweetalert2";

const TopBar = ({ onSave, onExit, canvasDataUrl }) => {
  const [productId, setProductId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setLanguage, t } = useLanguage();
  const { photos } = usePhotos();
  const [quantity, setQuantity] = useState(1);
  // Order source, same as ReviewOrderScreen's orderMode / selectedShop props
  const [orderMode, setOrderMode] = useState("");
  const [selectedShop, setSelectedShop] = useState("");

  // Parse product_id / quantity / order source from the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("product_id");
    const qty = params.get("quantity");

    const parsedProductId = Number.parseInt(pid, 10);
    const parsedQuantity = Number.parseInt(qty, 10);

    if (Number.isInteger(parsedProductId) && parsedProductId > 0) {
      setProductId(parsedProductId);
    }
    if (Number.isInteger(parsedQuantity) && parsedQuantity > 0) {
      setQuantity(parsedQuantity);
    }

    setOrderMode(params.get("orderMode") || params.get("order_mode") || "");
    setSelectedShop(params.get("shop") || "");
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setShowSettings(false);
  };

  // Generate image directly from <canvas> if the prop is missing
  const getCanvasImage = () => {
    if (canvasDataUrl) return canvasDataUrl;

    const konvaCanvas =
      document.querySelector(".konvajs-content canvas") ||
      document.querySelector("canvas");

    if (konvaCanvas) {
      return konvaCanvas.toDataURL("image/png");
    }

    console.warn("No canvas found to capture!");
    return null;
  };

  // Shows the real, non-hardcoded error message via the native browser
  // alert(). alert() is synchronous/blocking, so execution only resumes
  // (and the loader gets turned off) once the user clicks OK.
  const showFailureAlert = (message) => {
    window.alert(message);
    setLoading(false);
  };

  const getUploadedImageData = async (photo) => {
    if (photo?.dataUrl) return photo.dataUrl;
    if (!photo?.src) return null;

    try {
      const response = await fetch(photo.src);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Failed to read uploaded image:", error);
      return null;
    }
  };

  // Add to Cart API call
  const handleAddToCart = async () => {
    if (!Number.isInteger(productId) || productId < 1) {
      await Swal.fire({
        icon: "error",
        title: "Unable to add to cart",
        text: "No valid product ID was found.",
      });
      return;
    }

    // Use the globally exposed validation function (single or multi-page products)
    const missingResult = window.checkMissingPages
      ? await Promise.resolve(window.checkMissingPages())
      : [];
    const missing = Array.isArray(missingResult) ? missingResult : [];

    if (missing.length > 0) {
      const result = await Swal.fire({
        title: "Missing Content",
        html: `
            <p>The following areas do not have content:</p>
            <strong>${missing.join(", ")}</strong><br><br>
            <p>Please add content or continue anyway.</p>
            `,
        icon: "warning",
        iconColor: "#007AFF",
        showCancelButton: true,
        confirmButtonText: "Continue to Add to Cart",
        confirmButtonColor: "#007AFF",
        cancelButtonText: "Go Back to Edit",
      });

      if (!result.isConfirmed) {
        return; // user cancelled -> back to editor
      }
    }

    // Re-fetch images to ensure updated state after potential upload
    let images = [];
    try {
      if (window.getAllProductCanvasImages) {
        images = await window.getAllProductCanvasImages();
      }
    } catch (imageError) {
      console.error("Failed to capture product images:", imageError);
    }

    if (!Array.isArray(images) || images.length === 0) {
      const data = getCanvasImage();
      if (data) images = [{ name: "cover", data }];
    }

    if (!Array.isArray(images) || images.length === 0) {
      await Swal.fire({
        icon: "error",
        title: "Unable to add to cart",
        text: "No image data is available.",
      });
      return;
    }

    const pageNames = [
      "cover", "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December", "back"
    ];

    images = images
      .filter((img) => img && img.data)
      .map((img, i) => ({
        name: img.name || pageNames[i] || `page-${i + 1}`,
        data: img.data,
      }));

    if (images.length === 0) {
      await Swal.fire({
        icon: "error",
        title: "Unable to add to cart",
        text: "No image data is available.",
      });
      return;
    }

    const uploadedImages = (
      await Promise.all(
        (Array.isArray(photos) ? photos : []).map(async (photo) => ({
          name: photo.name || "uploaded-image",
          url: await getUploadedImageData(photo),
          tag: "user-uploaded image",
        }))
      )
    ).filter((image) => image.url);

    // Rough client-side heads-up if the payload is clearly going to be huge.
    // This does NOT block the request (the server is the source of truth
    // for its own post_max_size) — it's just an early, cheaper warning
    // than waiting on a round trip that's going to fail anyway.
    const approxBytes = [...images, ...uploadedImages].reduce(
      (sum, img) => sum + ((img.data || img.url)?.length || 0) * 0.75,
      0
    );
    const approxMB = approxBytes / (1024 * 1024);
    if (approxMB > 40) {
      const proceed = await Swal.fire({
        icon: "warning",
        title: "Large upload",
        html: `This order is roughly <strong>${approxMB.toFixed(1)}MB</strong>. Very large uploads can be rejected by the server. Continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Go Back",
      });
      if (!proceed.isConfirmed) return;
    }

    // Compute or read the subtotal
    let subtotal = Number.NaN;

    if (typeof window.subtotal === "function") {
      subtotal = Number.parseFloat(window.subtotal());
    }

    if (!Number.isFinite(subtotal)) {
      const params = new URLSearchParams(window.location.search);
      const urlPrice = Number.parseFloat(params.get("price"));
      if (Number.isFinite(urlPrice)) {
        subtotal = urlPrice;
      }
    }

    setLoading(true);

    try {
      // Unique key per cart row — prevents WooCommerce from merging rows,
      // same pattern as ReviewOrderScreen.jsx.
      const uniqueRowKey = `${Date.now()}_${Math.random().toString(36).substring(2)}`;

      // photos[] shape matches what the add-to-cart endpoint expects:
      // { photo, url, size, qty }. "size" has no meaning for a card
      // product so it's left blank; the backend keys the saved-image
      // record off "photo" (the page name).
      const orderData = {
        photos: images.map((img) => ({
          photo: img.name,
          url: img.data,
          size: "",
          qty: 1,
          unique_key: uniqueRowKey,
        })),
        options: {
          order_mode: orderMode,
          selected_shop: selectedShop,
          pages_count: images.length,
        },
        order_date: new Date().toISOString(),
        user_uploaded_images: uploadedImages,
        subtotal: Number.isFinite(subtotal) && subtotal >= 0
          ? Number(subtotal.toFixed(2))
          : undefined,
        unique_key: uniqueRowKey,
      };

      const siteUrl = "https://veyraprints.infinite-innovate.com";
      const addToCartUrl = `${siteUrl}/wp-json/cards/v2/add-to-cart`;

      const response = await axios.post(
        addToCartUrl,
        {
          product_id: productId,
          quantity: Math.max(1, Number.parseInt(quantity, 10) || 1),
          order_data: orderData,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        console.log("✅ Added to cart:", {
          cartItemKey: response.data.cart_item_key,
          cartCount: response.data.cart_count,
          savedImages: response.data.saved_images,
          orderData,
        });

        const cartUrl = `${siteUrl}/cart`;
        window.location.href = `${cartUrl}?t=${Date.now()}`;
        return;
      } else {
        // The backend returns { success:false, error: "..." } with a
        // specific message (e.g. "Upload too large (42.3MB)...", "No page
        // images were received...", etc). Surface that exact string —
        // no hardcoded fallback text is substituted here.
        showFailureAlert(response.data.error || response.data.message);
      }

    } catch (err) {
      // Axios wraps HTTP 4xx/5xx in err.response. A bare 500 with no JSON
      // body still falls into this branch — err.response?.data may be
      // empty/HTML, so build the most accurate real message available
      // rather than a hardcoded generic string.
      let serverMsg = err.response?.data?.error || err.response?.data?.message;

      if (!serverMsg && err.response?.status) {
        serverMsg = `Server returned HTTP ${err.response.status}: ${err.response.statusText || err.message}`;
      }

      if (!serverMsg) {
        serverMsg = err.message;
      }

      console.error("❌ Add-to-cart error:", err);
      showFailureAlert(serverMsg);
      return;
    }
    setLoading(false);
  };

  return (
    <>
      <div className="canvas-topbar">
        <div className="canvas-topbar-left">
          <button className="canvas-topbar-btn icon-btn" onClick={onExit} title={t("return")}></button>
          <span className="canvas-topbar-title">Veyra Prints</span>
        </div>

        <div className="canvas-topbar-right">
          <div className="settings-dropdown">
            <button
              className="canvas-topbar-btn icon-btn"
              onClick={() => setShowSettings(!showSettings)}
            >
              <i className="fa-solid fa-gear"></i> {t("settings")}
            </button>

            {showSettings && (
              <div className="settings-menu">
                <h4>{t("selectLanguage")}</h4>
                <ul>
                  <li onClick={() => handleLanguageChange("en")}>🇬🇧 English</li>
                  <li onClick={() => handleLanguageChange("fr")}>🇫🇷 Français</li>
                  <li onClick={() => handleLanguageChange("da")}>🇩🇰 Dansk</li>
                  <li><hr /></li>
                  <li style={{ cursor: "pointer" }} onClick={() => setShowSettings(false)}> <i className="fa-solid fa-circle-left"></i>&nbsp; Return</li>
                </ul>
              </div>
            )}
          </div>

          <button className="canvas-topbar-btn icon-btn" onClick={onSave}>
            <i className="fa-solid fa-floppy-disk"></i> {t("save")}
          </button>

          <button
            className="canvas-topbar-btn next-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {t("next")} <i className="fa-solid fa-arrow-right"></i>
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <div className="loading-text">Loading</div>
        </div>
      )}
    </>
  );
};

export default TopBar;